"use client";
import React, { useCallback, useEffect, useRef } from "react";
import dynamic from "next/dynamic";
import { useSearch } from "modules/common/stores/useCommonStore";
import { Modal } from "modules/common/components/Modal/Modal";
import type { UnifiedSearchRef } from "./UnifiedSearch";

// Dynamically import UnifiedSearch to avoid SSR issues with OpenStreetMapProvider
const UnifiedSearch = dynamic(
  () =>
    import("./UnifiedSearch").then((mod) => ({
      default: mod.UnifiedSearch,
    })),
  { ssr: false }
);

export function SearchOverlay() {
  const { searchOpen: showSearch, setSearchOpen: setShowSearch } = useSearch();
  const searchRef = useRef<UnifiedSearchRef>(null);

  // Focus the search input when the modal opens
  useEffect(() => {
    if (showSearch && searchRef.current) {
      // Small delay to ensure the modal animation has started
      const focusTimeout = setTimeout(() => {
        searchRef.current?.focus();
      }, 100);
      return () => clearTimeout(focusTimeout);
    }
  }, [showSearch]);

  // Close modal when navigation happens
  const handleNavigate = useCallback(() => {
    setShowSearch(false);
  }, [setShowSearch]);

  const handleClose = useCallback(() => {
    setShowSearch(false);
    searchRef.current?.reset();
  }, [setShowSearch]);

  return (
    <Modal
      open={showSearch}
      onClose={handleClose}
      title="Search"
      maxWidth="7xl"
      placement="top"
      showCloseButton
      allowOverflow
    >
      <UnifiedSearch
        ref={searchRef}
        onNavigate={handleNavigate}
        className="w-full max-w-none"
        tooltipPlacement="bottom"
      />
    </Modal>
  );
}
